'use client';

import R2Image from '@/components/ui/R2Image';

import SpecialityDropdownButton from './SpecialityDropdownButton';

interface SearchBarShellProps {
  searchTerm: string;
  inputPlaceholder: string;
  searchButtonLabel: string;
  selectedSpecTitle: string;
  showDropdown: boolean;
  suggestionCount: number;
  activeDescendantId?: string;
  openSpecialityDropdown: boolean;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onInputFocus: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onToggleSpecialityDropdown: () => void;
  onSearch: () => void;
}
/**
 * The visible search box: glass panel, text input, speciality trigger
 * and submit button. The results listbox is rendered by the parent so it
 * can escape the shell's `overflow-hidden` rounding.
 */
export default function SearchBarShell({
  searchTerm,
  inputPlaceholder,
  searchButtonLabel,
  selectedSpecTitle,
  showDropdown,
  suggestionCount,
  activeDescendantId,
  openSpecialityDropdown,
  onInputChange,
  onInputFocus,
  onKeyDown,
  onToggleSpecialityDropdown,
  onSearch,
}: SearchBarShellProps) {
  return (
    <div
      data-testid="gradient-hero-search-bar"
      className="border-border-subtle bg-surface-header-glass rounded-searchbar xl:h-searchbar-height relative z-50 border"
    >
      <div className="rounded-searchbar-inner h-full overflow-hidden backdrop-blur-md">
        <div className="bg-searchbar-fill rounded-searchbar-core relative h-full">
          <div
            aria-hidden="true"
            className="rounded-searchbar-core pointer-events-none absolute inset-0 z-0 overflow-hidden"
          >
            <div className="bg-theme-primary-light blur-searchbar absolute -top-1/5 left-0 aspect-square w-2/3 rounded-full opacity-80 sm:-top-4/5 sm:w-2/5" />
            <div className="bg-lavender-300 blur-searchbar-strong absolute -top-1/5 left-1/4 aspect-square w-2/3 rounded-full opacity-65 sm:-top-4/5 sm:w-2/5" />
            <div className="bg-info-300 blur-searchbar absolute -top-1/5 right-0 aspect-square w-2/3 rounded-full opacity-70 sm:-top-4/5 sm:w-2/5" />
          </div>
          <div className="xl:pl-p-sm xl:pr-searchbar-padding-right relative z-10 flex h-full flex-col items-stretch gap-3 px-4 py-2 sm:flex-row sm:items-center sm:gap-0 sm:py-3 xl:flex-none xl:justify-between xl:px-0 xl:py-0">
            <div className="xl:h-searchbar-slot-height xl:w-searchbar-row-width flex flex-col sm:flex-1 sm:flex-row sm:items-center xl:flex-none xl:justify-between">
              <div className="gradient-border rounded-searchbar xl:h-searchbar-input-height xl:w-searchbar-input-width my-2 flex w-full items-center bg-white/20 px-3 py-2.5 sm:min-w-0 sm:flex-1 md:my-0 xl:flex-none">
                <span
                  className="pointer-events-none mr-2.5 shrink-0"
                  aria-hidden="true"
                >
                  <R2Image
                    path="/images/icons/search-icon.svg"
                    alt=""
                    width={14}
                    height={14}
                  />
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={onInputChange}
                  onFocus={onInputFocus}
                  onKeyDown={onKeyDown}
                  placeholder={inputPlaceholder}
                  aria-label={inputPlaceholder}
                  aria-expanded={showDropdown && suggestionCount > 0}
                  aria-controls="search-autocomplete-listbox"
                  aria-activedescendant={activeDescendantId}
                  role="combobox"
                  autoComplete="off"
                  className="text-text-body placeholder:text-gray-475 w-full min-w-0 border-0 bg-transparent text-sm outline-none focus:ring-0"
                />
              </div>
              <div
                className="search-bar-divider mx-5 hidden h-8 w-px shrink-0 self-center sm:block xl:mx-0 xl:flex-none"
                aria-hidden="true"
              />
              <div className="relative my-2 sm:shrink-0 md:my-0 xl:flex-none">
                <SpecialityDropdownButton
                  label={selectedSpecTitle}
                  open={openSpecialityDropdown}
                  onClick={onToggleSpecialityDropdown}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={onSearch}
              className="btn--filled w-full rounded-md px-5 py-2.5 whitespace-nowrap hover:cursor-pointer sm:ml-8 sm:w-auto sm:shrink-0 sm:py-2 xl:ml-0 xl:flex xl:flex-none xl:items-center xl:justify-center"
            >
              {searchButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
